import { type PropsWithChildren, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import cn from '@/lib/utils/cn';

import {
  Button as ShadcnButton,
  type Props as ButtonProps,
} from '../shadcn/button';

type Props = PropsWithChildren<
  { isLoading?: boolean; onSubmit?: () => Promise<void> | void } & Omit<
    ButtonProps,
    'onSubmit'
  >
>;

export default function Button({
  onSubmit,
  children,
  isLoading: isLoadingProp,
  disabled,
  ...props
}: Props) {
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoading = isSubmitting || isLoadingProp;

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      await onSubmit?.();
    } catch (error) {
      Sentry.captureException(error);
    }

    setIsSubmitting(false);
  };

  return (
    <ShadcnButton
      onClick={handleSubmit}
      {...props}
      className={cn(props.className, 'relative')}
      type="submit"
      disabled={isLoading || disabled}
    >
      <>
        <span
          className={cn(
            'inline-flex items-center justify-center gap-2 whitespace-nowrap',
            { ['opacity-0']: isLoading },
          )}
        >
          {children ?? t('SAVE')}
        </span>

        <span className="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center">
          <Loader2
            className={cn('animate-spin', {
              ['hidden']: !isLoading,
            })}
          />
        </span>
      </>
    </ShadcnButton>
  );
}
